import type { SnakeDir, SnakeGame, SnakeStep } from "./engine";

/**
 * «مار» — THE BOARD'S PAINTER: one `<canvas>`, a whole number of DEVICE pixels per cell, painted from
 * an ATLAS of ready-made tiles.
 *
 * ⚠️ A STEP PAINTS ONLY THE CELLS IT CHANGED, ONE `drawImage` EACH: the cell the tail left, the new
 * tail end, the old head (now body), the new head, and the food and the bonus when they change — a
 * handful of blits every 60–120 ms and nothing in between (no `requestAnimationFrame`, no animation,
 * no timer of its own). The tiles are drawn ONCE, into the atlas, when the look, the cell size or the
 * DPR changes; a step does no path work at all. The whole board is painted only then and when a game
 * starts.
 *
 * ⚠️ EVERY TILE IS A WHOLE, OPAQUE CELL — its ground included — and depends on nothing but the cell
 * itself: what is on it (ground, the food, a quarter of the bonus, the snake), which of its four sides
 * the snake runs out through (towards its two neighbours along the body; one for the tail end and the
 * head), and which way the head looks. So one blit covers whatever the cell showed before, and a
 * step's few blits leave the canvas exactly as a full repaint would.
 *
 * ⚠️ ONE CONTINUOUS BODY: a body tile carries the band from side to side — straight, or a turn — and
 * the tail end and the head carry it in from their one side, so two consecutive cells always meet
 * with the same cross-section on their shared edge: no gap anywhere. Across the wrap, a cell on the
 * board's edge runs the band out to the edge. (Sides are read off the neighbours' positions, which is
 * unambiguous on any board of 3 × 3 cells or more.)
 *
 * TWO LOOKS. `modern` is our games': the felt's green, a gold tube with rounded turns, a head whose
 * eyes look the way it goes, a red apple, a gold star for the bonus — every colour read off
 * `theme.css`. `retro` is an old phone's backlit LCD, all our own drawing: a green screen, dark blocky
 * pixels on a 5 × 5 dot grid a cell, each casting the faint shadow such screens had.
 */

export type SnakeLook = "modern" | "retro";

/** The four sides of a cell, as bits. */
const UP = 1;
const RIGHT = 2;
const DOWN = 4;
const LEFT = 8;

/** Clockwise quarter turns that carry a tile's canonical side (LEFT) to `side`. */
const TURNS: Readonly<Record<number, number>> = { [LEFT]: 0, [UP]: 1, [RIGHT]: 2, [DOWN]: 3 };
/** Clockwise quarter turns that carry the canonical facing (right) to a facing. */
const FACE_TURNS: Readonly<Record<SnakeDir, number>> = { right: 0, down: 1, left: 2, up: 3 };
const FACE_DELTA: Readonly<Record<SnakeDir, readonly [number, number]>> = {
  up: [0, -1],
  down: [0, 1],
  left: [-1, 0],
  right: [1, 0],
};
const FACINGS: readonly SnakeDir[] = ["up", "right", "down", "left"];

/** The atlas: tiles in rows of this many, the bonus block in the rows below them. */
const COLS = 8;
const GROUND = 0;
const FOOD = 1;
/** A body cell runs through two sides: straight, or one of four turns. */
const BODY_MASKS = [LEFT | RIGHT, UP | DOWN, LEFT | UP, UP | RIGHT, RIGHT | DOWN, DOWN | LEFT];
const TAIL_MASKS = [UP, RIGHT, DOWN, LEFT];
/** The head runs into its neck through one side — or none, for a snake one cell long. */
const HEAD_MASKS = [0, UP, RIGHT, DOWN, LEFT];
const BODY_BASE = 2;
const TAIL_BASE = BODY_BASE + BODY_MASKS.length;
const HEAD_BASE = TAIL_BASE + TAIL_MASKS.length;
const TILES = HEAD_BASE + HEAD_MASKS.length * FACINGS.length;

type Rgb = readonly [number, number, number];

const css = (c: Rgb, alpha = 1): string =>
  alpha >= 1
    ? `rgb(${Math.round(c[0])}, ${Math.round(c[1])}, ${Math.round(c[2])})`
    : `rgba(${Math.round(c[0])}, ${Math.round(c[1])}, ${Math.round(c[2])}, ${alpha})`;
const mix = (a: Rgb, b: Rgb, t: number): Rgb => [
  a[0] + (b[0] - a[0]) * t,
  a[1] + (b[1] - a[1]) * t,
  a[2] + (b[2] - a[2]) * t,
];
const WHITE: Rgb = [255, 255, 255];
const BLACK: Rgb = [0, 0, 0];

/** A theme token as RGB: the `--c-*` channel triples ("232 194 104") and plain `#rrggbb` both. */
function token(style: CSSStyleDeclaration, name: string, fallback: Rgb): Rgb {
  const v = style.getPropertyValue(name).trim();
  const hex = /^#([0-9a-f]{6})$/i.exec(v);
  if (hex) {
    const n = parseInt(hex[1] as string, 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }
  const parts = v.split(/[\s,]+/).map(Number);
  if (parts.length === 3 && parts.every((p) => Number.isFinite(p))) return parts as unknown as Rgb;
  return fallback;
}

interface ModernPalette {
  ground: Rgb;
  /** The tube: its rim, its shaded sides, its colour, its highlight. */
  rim: Rgb;
  edge: Rgb;
  base: Rgb;
  light: Rgb;
  sclera: Rgb;
  pupil: Rgb;
  food: Rgb;
  stem: Rgb;
  leaf: Rgb;
}

/** Our look, off the theme in force (dark or light): the shades are mixed from the tokens. */
function modernPalette(): ModernPalette {
  const style = getComputedStyle(document.documentElement);
  const gold = token(style, "--c-secondary", [232, 194, 104]);
  const edge = mix(gold, BLACK, 0.22);
  const rim = mix(gold, BLACK, 0.62);
  return {
    ground: token(style, "--felt-2", [19, 78, 56]),
    rim,
    edge,
    base: gold,
    light: mix(gold, WHITE, 0.5),
    sclera: token(style, "--card-face-bg", [251, 250, 244]),
    pupil: mix(rim, BLACK, 0.55),
    food: token(style, "--danger", [229, 86, 76]),
    stem: token(style, "--wood", [58, 42, 25]),
    leaf: token(style, "--success", [79, 183, 131]),
  };
}

/**
 * THE LCD — a look, so not the theme's. The operator (2026-11-02): greener and darker than the first
 * #b7c68f, like an old phone's backlit screen. The screen is #8db04a: the hue a photographed lit
 * phone screen measures (81°), between the published green-LCD palettes in lightness; its pixels
 * #121a0a (7.2 : 1 on it), their shadow #738f3c (1.5 : 1).
 */
const LCD = {
  ground: [141, 176, 74] as Rgb,
  pixel: [18, 26, 10] as Rgb,
  /** The faint shadow each lit pixel casts on the screen behind it. */
  shadow: [115, 143, 60] as Rgb,
  /** The hairline between two lit dots, where they are big enough to show it (#2c3a19). */
  seam: [44, 58, 25] as Rgb,
};
/** An LCD colour as CSS — for the screen's veil (the 3-2-1, «مکث») over the board. */
export function lcdColour(part: "ground" | "pixel", alpha = 1): string {
  return css(LCD[part], alpha);
}

/**
 * The LCD's pictures, 5 × 5 dots a cell (the bonus 10 × 10), LEFT-connected and right-facing; `#` a
 * lit dot. The body band is the middle three rows. Turned for the other sides.
 */
const RETRO_STRAIGHT = [".....", "#####", "#####", "#####", "....."];
const RETRO_TURN = [".###.", "####.", "####.", "###..", "....."]; // in from the left, out at the top
const RETRO_TAIL = [".....", "##...", "####.", "##...", "....."];
/** Looking right: wider than the neck, a pointed snout, `o` its two eyes. */
const RETRO_HEAD = [".###.", "##o#.", "#####", "##o#.", ".###."];
const RETRO_ARM = [".....", "##...", "##...", "##...", "....."]; // a head's neck, from the left
const RETRO_FOOD = [".....", ".###.", ".#.#.", ".###.", "....."];
const RETRO_BONUS = [
  "..........",
  "....#.....",
  "...###....",
  "#########.",
  ".#######..",
  "..#####...",
  "..##.##...",
  ".##...##..",
  ".#.....#..",
  "..........",
];

/** A picture turned `q` quarter turns clockwise. */
function turnPicture(rows: readonly string[], q: number): string[] {
  let out = rows.map((r) => r);
  for (let k = 0; k < ((q % 4) + 4) % 4; k++) {
    const n = out.length;
    const next: string[] = [];
    for (let y = 0; y < n; y++) {
      let row = "";
      for (let x = 0; x < n; x++) row += (out[n - 1 - x] as string)[y] as string;
      next.push(row);
    }
    out = next;
  }
  return out;
}

/** Which side of `a` its neighbour `b` lies on — through the wrap too. */
function sideOf(a: number, b: number, grid: number): number {
  const ax = a % grid;
  const ay = Math.floor(a / grid);
  const bx = b % grid;
  const by = Math.floor(b / grid);
  if (ay === by) {
    if (bx === (ax + 1) % grid) return RIGHT;
    if (bx === (ax + grid - 1) % grid) return LEFT;
  } else if (ax === bx) {
    if (by === (ay + 1) % grid) return DOWN;
    if (by === (ay + grid - 1) % grid) return UP;
  }
  return 0;
}

export class SnakePainter {
  private readonly ctx: CanvasRenderingContext2D | null;
  /** Device pixels per cell. */
  private px = 0;
  private look: SnakeLook = "modern";
  private palette: ModernPalette | null = null;
  /** The tiles, for the look and size they were drawn at (null: to be drawn). */
  private atlas: HTMLCanvasElement | null = null;
  private readonly bodySlot = new Int8Array(16).fill(-1);
  private readonly tailSlot = new Int8Array(16).fill(-1);

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly grid: number,
    private readonly bonusSize: number = 2,
  ) {
    let ctx: CanvasRenderingContext2D | null = null;
    try {
      ctx = canvas.getContext("2d");
    } catch {
      ctx = null; // no canvas (a test DOM): every paint is a no-op
    }
    this.ctx = ctx;
    BODY_MASKS.forEach((mask, i) => (this.bodySlot[mask] = BODY_BASE + i));
    TAIL_MASKS.forEach((mask, i) => (this.tailSlot[mask] = TAIL_BASE + i));
  }

  /** Size the board: `px` DEVICE pixels per cell at `dpr`. A full paint must follow. */
  resize(px: number, dpr: number): void {
    const ratio = dpr > 0 && Number.isFinite(dpr) ? dpr : 1;
    const cell = Math.max(1, Math.round(px));
    if (cell !== this.px) this.dropAtlas();
    this.px = cell;
    const side = cell * this.grid;
    this.canvas.width = side;
    this.canvas.height = side;
    this.canvas.style.width = `${side / ratio}px`;
    this.canvas.style.height = `${side / ratio}px`;
  }

  /**
   * Pick the look (and re-read the theme's colours). A full paint must follow. The same look in the
   * same colours keeps its tiles — the screen calls this on every resize too.
   */
  setLook(look: SnakeLook): void {
    const palette = look === "modern" ? modernPalette() : null;
    if (look === this.look && JSON.stringify(palette) === JSON.stringify(this.palette)) return;
    this.look = look;
    this.palette = palette;
    this.dropAtlas();
  }

  /** Let the tiles go — their pixels at once: a WebView may keep a dropped canvas's memory a while. */
  private dropAtlas(): void {
    if (this.atlas) {
      this.atlas.width = 0;
      this.atlas.height = 0;
    }
    this.atlas = null;
  }

  /** The whole board: a new game, a resize, a look change. */
  full(game: SnakeGame | null): void {
    const ctx = this.ctx;
    if (!ctx || this.px === 0 || !this.ensureAtlas()) return;
    ctx.fillStyle = css(this.groundColour());
    ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
    if (!game) return;
    if (game.food !== null) this.blit(FOOD, game.food);
    const bonus = game.bonus;
    if (bonus) this.blitBonus(bonus.cell);
    for (let i = 0; i < game.cells.length; i++) this.paintSnake(game, i);
  }

  /** One step: only the cells it changed, each painted from what is on it now. */
  step(step: SnakeStep, game: SnakeGame): void {
    if (!this.ctx || this.px === 0 || !this.atlas) return;
    const n = game.cells.length;
    if (!step.moved) {
      // the crash: nothing moved, but the head turned to face what it ran into
      this.paintSnake(game, n - 1);
      return;
    }
    if (step.bonusGone !== null) {
      for (const cell of game.bonusCells(step.bonusGone)) this.paintCell(game, cell);
    }
    if (step.tail !== null) {
      this.paintCell(game, step.tail); // the cell it left
      this.paintSnake(game, 0); // the new tail end
    }
    if (n >= 2) this.paintSnake(game, n - 2);
    this.paintSnake(game, n - 1);
    if (step.food !== null) this.paintCell(game, step.food);
    if (step.bonusNew !== null) this.blitBonus(step.bonusNew);
  }

  private groundColour(): Rgb {
    return this.look === "retro" ? LCD.ground : (this.palette?.ground ?? [19, 78, 56]);
  }

  /** A changed cell, from what is on it now: the snake's ends, the food, the bonus, or ground. */
  private paintCell(game: SnakeGame, cell: number): void {
    const cells = game.cells;
    const n = cells.length;
    if (cell === cells[n - 1]) return this.paintSnake(game, n - 1);
    if (n >= 2 && cell === cells[n - 2]) return this.paintSnake(game, n - 2);
    if (cell === cells[0]) return this.paintSnake(game, 0);
    if (cell === game.food) return this.blit(FOOD, cell);
    const bonus = game.bonus;
    if (bonus && this.inBlock(cell, bonus.cell)) return this.blitBonusQuarter(cell, bonus.cell);
    this.blit(GROUND, cell);
  }

  /** The snake's `i`-th cell (tail first): its tile from its neighbours along the body. */
  private paintSnake(game: SnakeGame, i: number): void {
    const cells = game.cells;
    const n = cells.length;
    const cell = cells[i] as number;
    const g = this.grid;
    if (i === n - 1) {
      const mask = n >= 2 ? sideOf(cell, cells[n - 2] as number, g) : 0;
      const facing = FACINGS.indexOf(game.direction);
      this.blit(HEAD_BASE + HEAD_MASKS.indexOf(mask) * FACINGS.length + facing, cell);
    } else if (i === 0) {
      this.blit(this.tailSlot[sideOf(cell, cells[1] as number, g)] as number, cell);
    } else {
      const mask =
        sideOf(cell, cells[i - 1] as number, g) | sideOf(cell, cells[i + 1] as number, g);
      this.blit(this.bodySlot[mask] as number, cell);
    }
  }

  private inBlock(cell: number, corner: number): boolean {
    const dx = (cell % this.grid) - (corner % this.grid);
    const dy = Math.floor(cell / this.grid) - Math.floor(corner / this.grid);
    return dx >= 0 && dx < this.bonusSize && dy >= 0 && dy < this.bonusSize;
  }

  private blit(slot: number, cell: number): void {
    if (slot < 0) return;
    const px = this.px;
    const sx = (slot % COLS) * px;
    const sy = Math.floor(slot / COLS) * px;
    const dx = (cell % this.grid) * px;
    const dy = Math.floor(cell / this.grid) * px;
    (this.ctx as CanvasRenderingContext2D).drawImage(
      this.atlas as HTMLCanvasElement,
      sx,
      sy,
      px,
      px,
      dx,
      dy,
      px,
      px,
    );
  }

  /** The whole bonus block in one blit. */
  private blitBonus(corner: number): void {
    const px = this.px;
    const side = px * this.bonusSize;
    const sy = Math.ceil(TILES / COLS) * px;
    const dx = (corner % this.grid) * px;
    const dy = Math.floor(corner / this.grid) * px;
    (this.ctx as CanvasRenderingContext2D).drawImage(
      this.atlas as HTMLCanvasElement,
      0,
      sy,
      side,
      side,
      dx,
      dy,
      side,
      side,
    );
  }

  /** One cell of the bonus block. */
  private blitBonusQuarter(cell: number, corner: number): void {
    const px = this.px;
    const qx = (cell % this.grid) - (corner % this.grid);
    const qy = Math.floor(cell / this.grid) - Math.floor(corner / this.grid);
    const sy = Math.ceil(TILES / COLS) * px;
    (this.ctx as CanvasRenderingContext2D).drawImage(
      this.atlas as HTMLCanvasElement,
      qx * px,
      sy + qy * px,
      px,
      px,
      (cell % this.grid) * px,
      Math.floor(cell / this.grid) * px,
      px,
      px,
    );
  }

  // ── THE ATLAS ────────────────────────────────────────────────────────────────────────────────

  /** Draw every tile for the current look and size, once. False: no canvas to draw them on. */
  private ensureAtlas(): boolean {
    if (this.atlas) return true;
    if (this.look === "modern" && !this.palette) this.palette = modernPalette();
    const px = this.px;
    const atlas = document.createElement("canvas");
    atlas.width = Math.max(COLS, this.bonusSize) * px;
    atlas.height = (Math.ceil(TILES / COLS) + this.bonusSize) * px;
    let a: CanvasRenderingContext2D | null = null;
    try {
      a = atlas.getContext("2d");
    } catch {
      a = null;
    }
    if (!a) return false;
    const slot = (index: number): [number, number] => [
      (index % COLS) * px,
      Math.floor(index / COLS) * px,
    ];
    const tile = (index: number, draw: (c: CanvasRenderingContext2D, s: number) => void): void => {
      const [x, y] = slot(index);
      a.save();
      a.beginPath();
      a.rect(x, y, px, px);
      a.clip();
      a.translate(x, y);
      a.fillStyle = css(this.groundColour());
      a.fillRect(0, 0, px, px);
      draw(a, px);
      a.restore();
    };
    const retro = this.look === "retro";
    tile(GROUND, () => {});
    tile(FOOD, (c, s) => (retro ? this.retroPicture(c, s, RETRO_FOOD, 0) : this.apple(c, s)));
    BODY_MASKS.forEach((mask, i) =>
      tile(BODY_BASE + i, (c, s) => {
        const straight = mask === (LEFT | RIGHT) || mask === (UP | DOWN);
        const q = straight
          ? mask === (UP | DOWN)
            ? 1
            : 0
          : (TURNS[this.turnSide(mask)] as number);
        if (retro) this.retroPicture(c, s, straight ? RETRO_STRAIGHT : RETRO_TURN, q, mask);
        else this.turned(c, s, q, () => (straight ? this.tubeStraight(c, s) : this.tubeTurn(c, s)));
      }),
    );
    TAIL_MASKS.forEach((mask, i) =>
      tile(TAIL_BASE + i, (c, s) => {
        const q = TURNS[mask] as number;
        if (retro) this.retroPicture(c, s, RETRO_TAIL, q, mask);
        else this.turned(c, s, q, () => this.tubeTail(c, s));
      }),
    );
    HEAD_MASKS.forEach((mask, m) =>
      FACINGS.forEach((facing, f) =>
        tile(HEAD_BASE + m * FACINGS.length + f, (c, s) =>
          retro ? this.retroHead(c, s, mask, facing) : this.modernHead(c, s, mask, facing),
        ),
      ),
    );
    // the bonus block
    const by = Math.ceil(TILES / COLS) * px;
    const side = px * this.bonusSize;
    a.save();
    a.beginPath();
    a.rect(0, by, side, side);
    a.clip();
    a.translate(0, by);
    a.fillStyle = css(this.groundColour());
    a.fillRect(0, 0, side, side);
    if (retro) this.retroPicture(a, side, RETRO_BONUS, 0);
    else this.star(a, side);
    a.restore();
    this.atlas = atlas;
    return true;
  }

  /** A turn's two sides → the side that is LEFT in the canonical turn (in from the left, out at the top). */
  private turnSide(mask: number): number {
    if (mask === (LEFT | UP)) return LEFT;
    if (mask === (UP | RIGHT)) return UP;
    if (mask === (RIGHT | DOWN)) return RIGHT;
    return DOWN; // DOWN | LEFT
  }

  /** Run `draw` in the tile's frame turned `q` quarter turns clockwise about its centre. */
  private turned(c: CanvasRenderingContext2D, s: number, q: number, draw: () => void): void {
    c.save();
    c.translate(s / 2, s / 2);
    c.rotate((q * Math.PI) / 2);
    c.translate(-s / 2, -s / 2);
    draw();
    c.restore();
  }

  // ── OUR LOOK ─────────────────────────────────────────────────────────────────────────────────

  /** The tube's width and its rim, in device pixels. */
  private tube(s: number): { half: number; rim: number } {
    return { half: s * 0.35, rim: Math.max(1, s * 0.055) };
  }

  /** The tube's cross-section, darker sides round a highlight — symmetric, so a turn matches. */
  private tubeStops(g: CanvasGradient): CanvasGradient {
    const p = this.palette as ModernPalette;
    g.addColorStop(0, css(p.edge));
    g.addColorStop(0.24, css(p.base));
    g.addColorStop(0.5, css(p.light));
    g.addColorStop(0.76, css(p.base));
    g.addColorStop(1, css(p.edge));
    return g;
  }

  /** Canonical: from the left edge to the right one. */
  private tubeStraight(c: CanvasRenderingContext2D, s: number): void {
    const p = this.palette as ModernPalette;
    const { half, rim } = this.tube(s);
    const mid = s / 2;
    c.fillStyle = css(p.rim);
    c.fillRect(0, mid - half - rim, s, 2 * (half + rim));
    c.fillStyle = this.tubeStops(c.createLinearGradient(0, mid - half, 0, mid + half));
    c.fillRect(0, mid - half, s, 2 * half);
  }

  /** Canonical: in from the left, out at the top — a quarter of a ring round the top-left corner. */
  private tubeTurn(c: CanvasRenderingContext2D, s: number): void {
    const p = this.palette as ModernPalette;
    const { half, rim } = this.tube(s);
    const mid = s / 2;
    const ring = (inner: number, outer: number): void => {
      c.beginPath();
      c.arc(0, 0, outer, 0, Math.PI * 2);
      c.arc(0, 0, Math.max(0, inner), Math.PI * 2, 0, true);
      c.fill();
    };
    c.fillStyle = css(p.rim);
    ring(mid - half - rim, mid + half + rim);
    c.fillStyle = this.tubeStops(c.createRadialGradient(0, 0, mid - half, 0, 0, mid + half));
    ring(mid - half, mid + half);
  }

  /** Canonical: in from the left, tapering to a rounded tip. */
  private tubeTail(c: CanvasRenderingContext2D, s: number): void {
    const p = this.palette as ModernPalette;
    const { half, rim } = this.tube(s);
    const mid = s / 2;
    const tipX = s * 0.62;
    const tip = half * 0.3;
    const shape = (h: number, t: number): void => {
      c.beginPath();
      c.moveTo(0, mid - h);
      c.lineTo(tipX, mid - t);
      c.arc(tipX, mid, t, -Math.PI / 2, Math.PI / 2);
      c.lineTo(0, mid + h);
      c.closePath();
      c.fill();
    };
    c.fillStyle = css(p.rim);
    shape(half + rim, tip + rim);
    c.fillStyle = this.tubeStops(c.createLinearGradient(0, mid - half, 0, mid + half));
    shape(half, tip);
  }

  /** The head: its neck in from `mask`'s side, a round head, two eyes looking `facing`. */
  private modernHead(c: CanvasRenderingContext2D, s: number, mask: number, facing: SnakeDir): void {
    const p = this.palette as ModernPalette;
    const { half, rim } = this.tube(s);
    const mid = s / 2;
    const r = s * 0.44;
    const neck = (h: number): void => c.fillRect(0, mid - h, mid, 2 * h);
    const turn = mask === 0 ? 0 : (TURNS[mask] as number);
    // the rim, then the colour
    this.turned(c, s, turn, () => {
      c.fillStyle = css(p.rim);
      if (mask !== 0) neck(half + rim);
      c.beginPath();
      c.arc(mid, mid, r + rim, 0, Math.PI * 2);
      c.fill();
      if (mask !== 0) {
        c.fillStyle = this.tubeStops(c.createLinearGradient(0, mid - half, 0, mid + half));
        neck(half);
      }
    });
    const [fx, fy] = FACE_DELTA[facing];
    // a dome, lit a little ahead of its centre
    const dome = c.createRadialGradient(mid + fx * s * 0.06, mid + fy * s * 0.06, 0, mid, mid, r);
    dome.addColorStop(0, css(p.light));
    dome.addColorStop(0.55, css(p.base));
    dome.addColorStop(1, css(p.edge));
    c.fillStyle = dome;
    c.beginPath();
    c.arc(mid, mid, r, 0, Math.PI * 2);
    c.fill();
    // two eyes on its front half, the pupils turned the way it goes
    const white = Math.max(1, s * 0.14);
    const pupil = Math.max(0.75, s * 0.08);
    for (const sideways of [-1, 1]) {
      const ex = mid + fx * s * 0.1 - fy * sideways * s * 0.2;
      const ey = mid + fy * s * 0.1 + fx * sideways * s * 0.2;
      c.fillStyle = css(p.sclera);
      c.beginPath();
      c.arc(ex, ey, white, 0, Math.PI * 2);
      c.fill();
      c.fillStyle = css(p.pupil);
      c.beginPath();
      c.arc(ex + fx * s * 0.045, ey + fy * s * 0.045, pupil, 0, Math.PI * 2);
      c.fill();
    }
  }

  /** The food: a red apple with a stem, a leaf and a shine. */
  private apple(c: CanvasRenderingContext2D, s: number): void {
    const p = this.palette as ModernPalette;
    const cx = s / 2;
    const cy = s * 0.57;
    const r = s * 0.38;
    const body = c.createRadialGradient(cx - r * 0.35, cy - r * 0.35, r * 0.1, cx, cy, r);
    body.addColorStop(0, css(mix(p.food, WHITE, 0.35)));
    body.addColorStop(0.5, css(p.food));
    body.addColorStop(1, css(mix(p.food, BLACK, 0.35)));
    c.fillStyle = css(mix(p.food, BLACK, 0.55));
    c.beginPath();
    c.arc(cx, cy, r + Math.max(0.75, s * 0.035), 0, Math.PI * 2);
    c.fill();
    c.fillStyle = body;
    c.beginPath();
    c.arc(cx, cy, r, 0, Math.PI * 2);
    c.fill();
    // the stem and the leaf
    c.strokeStyle = css(p.stem);
    c.lineWidth = Math.max(1, s * 0.065);
    c.lineCap = "round";
    c.beginPath();
    c.moveTo(cx, cy - r * 0.7);
    c.quadraticCurveTo(cx + s * 0.01, cy - r - s * 0.06, cx + s * 0.06, cy - r - s * 0.11);
    c.stroke();
    c.fillStyle = css(p.leaf);
    c.beginPath();
    c.ellipse(cx + s * 0.14, cy - r - s * 0.05, s * 0.11, s * 0.05, -0.45, 0, Math.PI * 2);
    c.fill();
    // the shine
    c.fillStyle = "rgba(255, 255, 255, 0.7)";
    c.beginPath();
    c.ellipse(cx - r * 0.4, cy - r * 0.35, r * 0.2, r * 0.12, -0.8, 0, Math.PI * 2);
    c.fill();
  }

  /** The bonus: a gold star on a soft glow, over its whole block (`side` device pixels). */
  private star(c: CanvasRenderingContext2D, side: number): void {
    const p = this.palette as ModernPalette;
    const cx = side / 2;
    const cy = side / 2 + side * 0.03;
    const glow = c.createRadialGradient(cx, cy, 0, cx, cy, side / 2);
    glow.addColorStop(0, css(p.light, 0.5));
    glow.addColorStop(0.6, css(p.base, 0.18));
    glow.addColorStop(1, css(p.base, 0));
    c.fillStyle = glow;
    c.fillRect(0, 0, side, side);
    const outer = side * 0.4;
    const inner = outer * 0.48;
    c.beginPath();
    for (let i = 0; i < 10; i++) {
      const radius = i % 2 === 0 ? outer : inner;
      const angle = -Math.PI / 2 + (i * Math.PI) / 5;
      const x = cx + radius * Math.cos(angle);
      const y = cy + radius * Math.sin(angle);
      if (i === 0) c.moveTo(x, y);
      else c.lineTo(x, y);
    }
    c.closePath();
    c.lineJoin = "round";
    c.lineWidth = Math.max(1.5, side * 0.07);
    c.strokeStyle = css(p.rim);
    c.stroke();
    const fill = c.createLinearGradient(0, cy - outer, 0, cy + outer);
    fill.addColorStop(0, css(p.light));
    fill.addColorStop(0.55, css(p.base));
    fill.addColorStop(1, css(p.edge));
    c.fillStyle = fill;
    c.fill();
    // a glint
    c.fillStyle = "rgba(255, 255, 255, 0.85)";
    c.beginPath();
    c.ellipse(
      cx - outer * 0.2,
      cy - outer * 0.25,
      outer * 0.12,
      outer * 0.07,
      -0.6,
      0,
      Math.PI * 2,
    );
    c.fill();
  }

  // ── THE LCD ──────────────────────────────────────────────────────────────────────────────────

  /**
   * A dot picture over a `size` square, turned `q` quarter turns, each lit dot casting its shadow
   * down and to the right. `joined` names the sides the band runs on through: there the picture
   * goes on past the edge (into the next cell), so its shadow is drawn on up to the edge as well.
   */
  private retroPicture(
    c: CanvasRenderingContext2D,
    size: number,
    rows: readonly string[],
    q: number,
    joined = 0,
  ): void {
    const picture = turnPicture(rows, q);
    const n = picture.length;
    const at = (k: number): number => Math.round((k * size) / n);
    const lit: [number, number][] = [];
    picture.forEach((row, y) => {
      for (let x = 0; x < n; x++) if (row[x] === "#") lit.push([x, y]);
    });
    // the dots just past a joined edge, where the neighbour's band goes on
    const beyond: [number, number][] = [];
    for (const [x, y] of lit) {
      if (joined & LEFT && x === 0) beyond.push([-1, y]);
      if (joined & UP && y === 0) beyond.push([x, -1]);
    }
    const shift = Math.max(1, Math.round((size / n) * 0.22));
    c.fillStyle = css(LCD.shadow);
    for (const [x, y] of [...lit, ...beyond]) {
      const x0 = x < 0 ? -size : at(x);
      const y0 = y < 0 ? -size : at(y);
      const x1 = x < 0 ? 0 : at(x + 1);
      const y1 = y < 0 ? 0 : at(y + 1);
      c.fillRect(x0 + shift, y0 + shift, x1 - x0, y1 - y0);
    }
    // the dots: a hairline on their right and bottom where they are 4 device pixels or more
    const hair = size / n >= 4 ? 1 : 0;
    if (hair) {
      c.fillStyle = css(LCD.seam);
      for (const [x, y] of lit) c.fillRect(at(x), at(y), at(x + 1) - at(x), at(y + 1) - at(y));
    }
    c.fillStyle = css(LCD.pixel);
    for (const [x, y] of lit) {
      c.fillRect(at(x), at(y), at(x + 1) - at(x) - hair, at(y + 1) - at(y) - hair);
    }
  }

  /**
   * The LCD's head: looking `facing`, its neck in from `mask`'s side. A neck that comes in beside it
   * (a head that turned to face what it ran into) covers the eye on that side, so the body stays
   * joined.
   */
  private retroHead(c: CanvasRenderingContext2D, s: number, mask: number, facing: SnakeDir): void {
    const head = turnPicture(RETRO_HEAD, FACE_TURNS[facing]);
    const arm = mask === 0 ? null : turnPicture(RETRO_ARM, TURNS[mask] as number);
    const rows = head.map((row, y) =>
      [...row].map((dot, x) => (dot === "#" || arm?.[y]?.[x] === "#" ? "#" : ".")).join(""),
    );
    this.retroPicture(c, s, rows, 0, mask);
  }
}
